import { validatePrintSelection, validatePrintFile, hasImageSignature, printTotals, printFormData, isCompletePrintGroup, managedGroups, regularOrderSizes } from './printocom-order.js';

if (!customElements.get('printocom-customizer')) {
  customElements.define('printocom-customizer', class extends HTMLElement {
    connectedCallback() {
      this.config = JSON.parse(this.querySelector('[data-pc-customizer-config]').textContent);
      this.form = this.closest('form');
      this.productInfo = this.closest('product-info');
      this.quantity = this.productInfo.querySelector('input[name="quantity"]');
      this.file = this.querySelector('[data-pc-file]');
      this.error = this.querySelector('[data-pc-native-error]');
      this.addEventListener('change', this.onChange);
      window.addEventListener('pageshow', this.onPageShow);
      this.quantity?.addEventListener('input', this.onChange);
      this.quantity?.addEventListener('change', this.onChange);
      this.form.querySelector('[name="id"]')?.addEventListener('change', this.onChange);
      this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (data.sectionId === this.productInfo.dataset.section) {
          this.updateSizes();
          this.updateTotals();
        }
      });
      const button = this.form.querySelector('[data-pc-native-submit]');
      button.disabled = button.dataset.pcNativeAvailable !== 'true';
      this.updateSizes();
      this.updateTotals();
    }

    disconnectedCallback() {
      this.quantity?.removeEventListener('input', this.onChange);
      this.quantity?.removeEventListener('change', this.onChange);
      this.variantUnsubscriber?.();
      window.removeEventListener('pageshow', this.onPageShow);
      if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
    }

    onChange = (event) => {
      this.error.hidden = true;
      if (event.target === this.file) this.updatePreview();
      this.updateTotals();
    };

    onPageShow = async () => {
      this.updateTotals();
      this.updatePreview();
      try {
        const response = await fetch(routes.cart_url + '?section_id=cart-icon-bubble', { cache: 'no-store' });
        if (!response.ok) return;
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const icon = document.getElementById('cart-icon-bubble');
        const section = html.querySelector('.shopify-section');
        if (icon && section) icon.innerHTML = section.innerHTML;
      } catch {}
    };

    updateSizes() {
      const id = this.form.querySelector('[name="id"]').value;
      const color = this.config.variants.find(variant => variant.id === id)?.title;
      this.catalogSizes = regularOrderSizes(this.config.sizeByColor, color, this.dataset.sizes.split(','));
      for (const input of this.querySelectorAll('[data-pc-size]')) {
        const available = this.catalogSizes.includes(input.value);
        input.disabled = !available;
        input.closest('label').hidden = !available;
        if (!available) input.checked = false;
      }
    }

    state() {
      const size = this.querySelector('[data-pc-size]:checked')?.value || '';
      const quantity = this.quantity;
      const id = this.form.querySelector('[name="id"]').value;
      return { optionsReady: !this.productInfo.pendingRequestUrl, sizeSelected: Boolean(size), selectedSize: size, catalogSizes: this.catalogSizes || [], quantity: quantity.value, min: Number(quantity.min) || 1, max: Math.min(quantity.max ? Number(quantity.max) : Infinity, 999), step: Number(quantity.step) || 1, prints: [...this.querySelectorAll('[data-pc-print]:checked')].map(input => ({ id: input.value, title: input.dataset.title, price: Number(input.dataset.price) })), file: this.file.files[0], confirmed: this.querySelector('[data-pc-confirm]').checked, variant: this.config.variants.find(variant => variant.id === id), size };
    }

    showError(code) {
      this.error.textContent = this.config.messages[code] || this.config.messages.requestError;
      this.error.hidden = false;
      this.error.focus();
    }

    updateTotals() {
      const state = this.state();
      const totals = printTotals(state.variant?.price, state.prints, Number(state.quantity));
      const money = new Intl.NumberFormat(this.config.locale, { style: 'currency', currency: this.config.currency });
      this.querySelector('[data-pc-unit]').textContent = totals ? money.format(totals.unit / 100) : '—';
      this.querySelector('[data-pc-total]').textContent = totals ? money.format(totals.total / 100) : '—';
    }

    updatePreview() {
      if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
      const preview = this.querySelector('.pc-customizer__preview');
      const file = this.file.files[0];
      preview.hidden = true;
      if (!file) return;
      const error = validatePrintFile(file);
      if (error) return this.showError(error);
      this.previewUrl = URL.createObjectURL(file);
      this.querySelector('[data-pc-preview]').src = this.previewUrl;
      this.querySelector('[data-pc-file-name]').textContent = file.name;
      preview.hidden = false;
    }

    async addToCart(productForm) {
      if (this.busy || productForm.submitButton.disabled) return;
      const state = this.state();
      const error = validatePrintSelection(state);
      if (error) return this.showError(error);
      if (!state.variant?.available) return this.showError('unavailable');
      const source = new FormData(this.form);
      const controls = [...this.productInfo.querySelectorAll('input,select,textarea,button')].map(input => [input, input.disabled]);
      controls.forEach(([input]) => { input.disabled = true; });
      this.busy = true;
      productForm.submitButton.setAttribute('aria-disabled', 'true');
      productForm.submitButton.classList.add('loading');
      productForm.querySelector('.loading__spinner')?.classList.remove('hidden');
      this.querySelector('[data-pc-upload-status]').hidden = false;
      this.error.hidden = true;
      const group = crypto.randomUUID();
      try {
        const bytes = new Uint8Array(await state.file.slice(0, 12).arrayBuffer());
        if (!hasImageSignature(bytes, state.file.type)) return this.showError('fileType');
        const formData = printFormData(source, { ...state, quantity: Number(state.quantity), title: this.config.title, group, imageKey: this.file.name.slice(11, -1), printKey: this.config.messages.printKey, targetKey: this.config.messages.targetKey });
        const response = await fetch(routes.cart_add_url + '.js', { method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' }, body: formData });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error('requestError');
        const cart = await this.readCart();
        if (!isCompletePrintGroup(cart, group, state.prints.map(print => print.id))) throw new Error('incomplete');
        window.location.href = routes.cart_url;
      } catch (failure) {
        try {
          const cart = await this.readCart();
          if (isCompletePrintGroup(cart, group, state.prints.map(print => print.id))) {
            window.location.href = routes.cart_url;
            return;
          }
          const groupItems = managedGroups(cart).get(group) || [];
          if (groupItems.length) {
            const updates = Object.fromEntries(groupItems.map(item => [item.key, 0]));
            const rollback = await fetch(routes.cart_update_url + '.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ updates }) });
            if (!rollback.ok || managedGroups(await this.readCart()).has(group)) throw new Error('incomplete');
          }
          this.showError(failure.message === 'incomplete' ? 'incomplete' : 'requestError');
        } catch {
          this.showError('incomplete');
        }
      } finally {
        controls.forEach(([input, disabled]) => { input.disabled = disabled; });
        this.busy = false;
        productForm.submitButton.removeAttribute('aria-disabled');
        productForm.submitButton.classList.remove('loading');
        productForm.querySelector('.loading__spinner')?.classList.add('hidden');
        this.querySelector('[data-pc-upload-status]').hidden = true;
      }
    }

    async readCart() {
      const response = await fetch(routes.cart_url + '.js', { cache: 'no-store' });
      if (!response.ok) throw new Error('requestError');
      return response.json();
    }
  });
}
